import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./profilepage.css";
import BackButton from "../../generic/elements/backbutton/BackButton";
import { Col, notification, Row, Spin } from "antd";
import { executeIntent } from "../../utils/IntentExecutor";
import IntentType from "../../../intentexecutor/utils/IntentType";
import ServicesSettings from "../servicesettings/ServicesSettings";
import MemoryType from "../../../service/memory/enum/MemoryType";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import ProfileOverview from "../../../components/generic/profileoverview/ProfileOverview";
import AddEditWorkforceModal from "../../carecoordinator/workforce/AddEditWorkforceModal";
import { getContext } from "../../../intentexecutor/contextcreator/ContextCreator";
import { useRecoilValue } from "recoil";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../utils/configurationconstants";
import { mapWorkforceToProfileData } from "../../../components/generic/profiledataextractor/profileDataExtractor";
import { useObservable } from "../../generic/hooks/useObservable";
import { notifiyUser } from "../../utils/Notification";
import { strings } from "../../utils/strings";
import { workforceDataUpdateResponse } from "../../carecoordinator/workforce/AddEditWorkforceModal";
import CarePlan from "../../carecoordinator/careplan/CarePlan";
import apiService from "../../../service/apiservice/apiService";
import { EventType } from "../../../service/network/utils/EventType";
import CustomModal from "../../../components/generic/custommodal/CustomModal";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import { getFullName } from "../../careprovider/visits/utils";
import { useSearchParams } from "react-router-dom";
import { getMediaKeyObjectStore } from "../../../components/generic/formbuilder/networkservice/FormService";
import SectionCard from "../../generic/sectioncard/SectionCard";
import DocumentsSettings from "./documentssettings/DocumentsSettings";
import VolunteerStatisticsWrapper from "./VolunteerStatisticsWrapper";
import AssignedPatientsWrapper from "./AssignedPatientsWrapper";
import { officeLabel } from "../../../components/recoil/officeLabelRecoil";
import SecondaryButton from "../../../components/generic/buttons/SecondaryButton";
import { publishEvent } from "../../../components/generic/EventPublisher";
import VolunteerPrefernceForm from "../../../components/forms/VolunteerPrefernceForm";

const VOLUNTEER_PREFERENCES_FORM_ID = "volunteer-profile-preferences-form";

// Kept at module scope so the reference stays stable across renders, otherwise
// CarePlan refetches on every re-render of this page.
const ACTIVE_CARE_PLAN_STATUS = [{ value: "Active" }];
const ALL_CARE_PLAN_STATUS = [];

const VolunteerProfilePage = () => {
  const [searchParams] = useSearchParams();
  const user = useReadMemory(MemoryType.USER);
  const identifier = searchParams.get("identifier") || user?.userIdentifier;
  const isVolunteer = user?.jobTitle === "Volunteer";
  const isVolunteerCoordinator = user?.jobTitle === "Volunteer Coordinator";
  const carePlanStatus = isVolunteer
    ? ACTIVE_CARE_PLAN_STATUS
    : ALL_CARE_PLAN_STATUS;
  const [selectedServices, setSelectedServices] = useState([]);
  const [services, setServices] = useState([]);
  const [addedService, setAddedService] = useState(undefined);
  const [isServicesLoading, setIsServicesLoading] = useState(false);
  const [isSelectedServicesLoading, setIsSelectedServicesLoading] =
    useState(false);
  const [userDetails, setUserDetails] = useState(undefined);
  const organizationIdentifier = useGetOrgIdentifier();
  const [reloadToken, setReloadToken] = useState(0);
  const label = useRecoilValue(officeLabel);

  // A counter instead of a boolean avoids the second fetch that a reset back to
  // `false` would trigger through the effect dependency.
  const handleWorkforceDataUpdate = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);
  useObservable(workforceDataUpdateResponse, handleWorkforceDataUpdate);

  // Feature Access settings
  const configuration = useRecoilValue(currentConfiguration);
  const bucketName =
    configuration[ConfigurationConstants.NIRAMAYA_FORMS_BUCKETNAME];
  const isUserProfileDetailsCardAllowed =
    configuration[
      ConfigurationConstants.FEATUREACCESS_PROFILE_VOLUNTEER_PROFILE_DETAILS
    ]?.visibility;
  const isServicesOfferedCardAllowed =
    configuration[
      ConfigurationConstants.FEATUREACCESS_PROFILE_VOLUNTEER_SERVICES_OFFERED
    ]?.visibility;
  const allowProfileEdit =
    configuration[ConfigurationConstants.FEATUREACCESS_VOLUNTEER_PROFILE_EDIT]
      ?.visibility;
  const allowResetPin =
    configuration[ConfigurationConstants.FEATUREACCESS_VOLUNTEER_RESET_PIN]
      ?.visibility;
  const [formLoading, setFormLoading] = useState(false);
  const [openEditVolunteerModal, setOpenEditVolunteerModal] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [volunteerPreferenceOptions, setVolunteerPreferenceOptions] =
    useState(null);

  const getServices = useCallback(async () => {
    setIsServicesLoading(true);
    const payload = {
      configurationKey: ["niramaya.organization.volunteerduties"],
    };

    const result = await executeIntent(IntentType.GET_CONFIGURATION, payload, {
      organizationIdentifier: organizationIdentifier,
    });
    const service =
      result[0].configurationValue && JSON.parse(result[0].configurationValue);
    setServices(service);
    setIsServicesLoading(false);
  }, [organizationIdentifier]);

  const addServices = useCallback(
    async (_, service) => {
      const payload = {
        data: {
          organizationIdentifier: organizationIdentifier,
          userIdentifier: identifier,
          serviceTitle: service.title,
          serviceKey: service.serviceKey,
          creatorIdentifier: user?.userIdentifier,
          givenName: userDetails?.givenName,
          familyName: userDetails?.familyName,
          serviceIdentifier: service?.serviceIdentifier,
        },
      };
      const result = await executeIntent(
        IntentType.ADD_WORKFORCE_SERVICE,
        payload,
        {
          organizationIdentifier: organizationIdentifier,
        },
      );
      notifiyUser(
        result,
        strings.SERVICES_ADDED_SUCCESS,
        strings.SERVICES_ADDED_ERROR,
      );
      setAddedService(result);
    },
    [
      organizationIdentifier,
      identifier,
      user?.userIdentifier,
      userDetails?.givenName,
      userDetails?.familyName,
    ],
  );

  const deleteService = useCallback(
    async (_, service) => {
      const payload = {
        data: {
          serviceIdentifier: service.serviceIdentifier,
          userIdentifier: identifier,
          creatorIdentifier: user?.userIdentifier,
          givenName: userDetails?.givenName,
          familyName: userDetails?.familyName,
        },
      };

      const result = await executeIntent(
        IntentType.DELETE_WORKFORCE_SERVICE,
        payload,
        {},
      );
      notifiyUser(
        result,
        strings.SERVICES_DELETED_SUCCESS,
        strings.SERVICES_DELETED_ERROR,
      );
    },
    [
      identifier,
      user?.userIdentifier,
      userDetails?.givenName,
      userDetails?.familyName,
    ],
  );

  const handleResetPin = useCallback(
    async (userIdentifier) => {
      const payload = {
        userIdentifier,
      };
      const response = await apiService.post(
        "niramayafoundation/auth/api/v1/resetpin",
        payload,
      );
      if (response.value) {
        setOpenModal(true);
        workforceDataUpdateResponse.next({
          type: EventType.WORKFORCE_PIN_RESET,
        });
        publishEvent("niramaya.volunteer.pin.reset", user, userDetails);
      } else {
        notification.error({
          message: "Could not reset pin",
          placement: "bottomRight",
        });
      }
    },
    [user, userDetails],
  );

  const footerButtons = useMemo(
    () => (
      <>
        <PrimaryButton
          buttonText={"OK"}
          handleOnClick={() => setOpenModal(false)}
        />
      </>
    ),
    [],
  );

  const volunteerPreferencesModalFooter = useMemo(
    () => (
      <>
        <SecondaryButton
          buttonText="Cancel"
          handleOnClick={() => setOpenForm(false)}
        />
        <PrimaryButton
          buttonText="Add"
          htmlType="submit"
          form={VOLUNTEER_PREFERENCES_FORM_ID}
          loading={formLoading}
        />
      </>
    ),
    [formLoading],
  );

  useEffect(() => {
    let isCurrent = true;

    const getUserDetails = async () => {
      const payload = {
        userIdentifier: identifier,
      };
      const userData = await executeIntent(
        IntentType.GET_WORKFORCE_DETAILS_BY_USER_IDENTIFIER,
        payload,
      );

      if (userData[0]?.profilePicture) {
        const icon = await getMediaKeyObjectStore(
          userData[0]?.profilePicture,
          bucketName?.value,
        );
        if (!isCurrent) return;
        if (icon) {
          setUserDetails({ ...userData[0], icon });
        } else {
          setUserDetails(userData[0]);
        }
      } else {
        if (!isCurrent) return;
        setUserDetails(userData[0]);
      }
    };

    getUserDetails();

    return () => {
      isCurrent = false;
    };
  }, [bucketName?.value, identifier, reloadToken]);

  const userProfileData = useMemo(() => {
    const actions = [
      allowProfileEdit && {
        label: "Edit",
        icon: "ICON_FOR_PROFILE_EDIT",
        map: "ICON_FOR_PROFILE_EDIT",
        functionName: () => {
          setOpenEditVolunteerModal(true);
        },
      },
      {
        label: "Add Details",
        icon: "ICON_PREFERENCE",
        map: "ICON_PREFERENCE",
        functionName: () => {
          setOpenForm(true);
        },
      },
      allowResetPin &&
        userDetails?.hasPin && {
          label: "Reset Pin",
          icon: "ICON_FOR_RESET_PIN",
          map: "ICON_FOR_RESET_PIN",
          functionName: (workforce) => {
            handleResetPin(workforce.userIdentifier);
          },
        },
    ].filter(Boolean);

    const volunteerProfile = mapWorkforceToProfileData(
      userDetails,
      label,
      true,
      true,
      volunteerPreferenceOptions,
    );
    return {
      ...volunteerProfile,
      actionIcons: actions,
      actionData: userDetails,
    };
  }, [
    allowProfileEdit,
    allowResetPin,
    handleResetPin,
    label,
    userDetails,
    volunteerPreferenceOptions,
  ]);

  useEffect(() => {
    let isCurrent = true;

    const getAddedServices = async () => {
      setIsSelectedServicesLoading(true);
      const payload = {
        userIdentifier: identifier,
      };
      const result = await executeIntent(
        IntentType.GET_WORKFORCE_SERVICE,
        payload,
        getContext(),
      );
      if (!isCurrent) return;
      setSelectedServices(result);
      setIsSelectedServicesLoading(false);
    };

    getAddedServices();

    return () => {
      isCurrent = false;
    };
  }, [identifier, services]);

  useEffect(() => {
    let isCurrent = true;

    const getPreferenceOptions = async () => {
      const payload = {
        configurationKey: ["niramaya.volunteer.preference.options"],
      };

      const result = await executeIntent(
        IntentType.GET_CONFIGURATION,
        payload,
        {
          organizationIdentifier,
        },
      );
      const options =
        result[0].configurationValue &&
        JSON.parse(result[0].configurationValue);

      if (!isCurrent) return;
      setVolunteerPreferenceOptions(options);
    };

    getPreferenceOptions();

    return () => {
      isCurrent = false;
    };
  }, [organizationIdentifier]);

  return (
    <section className="section-content">
      <div className="form-renderer-back-button">
        <BackButton />
        <p>Workforce Profile Page</p>
      </div>
      <Row className="profile-card-styling">
        {isUserProfileDetailsCardAllowed && (
          <Col xs={24} lg={24}>
            <div className="patient-profile-overview-shell">
              {userDetails !== undefined && userProfileData?.overviewConfig ? (
                <>
                  <ProfileOverview
                    overviewConfig={userProfileData.overviewConfig}
                    actionIcons={userProfileData.actionIcons}
                    actionData={userProfileData.actionData}
                  />
                  {openEditVolunteerModal && (
                    <AddEditWorkforceModal
                      openAddWorkforceModal={openEditVolunteerModal}
                      setOpenAddWorkforceModal={setOpenEditVolunteerModal}
                      editVolunteer={true}
                      userIdentifier={userDetails?.userIdentifier}
                      showAdditionalFields={true}
                      disableEmail={true}
                    />
                  )}
                </>
              ) : (
                <Spin className="d-flex justify-content-center m-auto py-5" />
              )}
            </div>
          </Col>
        )}
      </Row>
      <Row gutter={[16, 16]}>
        <VolunteerStatisticsWrapper workforceIdentifier={identifier} />
        <Col xs={24} xl={12}>
          <SectionCard
            title="Volunteer Request"
            className="listCardStylingThirdRow"
          >
            <CarePlan
              workforceIdentifier={identifier}
              carePlanStatus={carePlanStatus}
              carePlanClass="px-3"
              spinnerClass="spinnerCenter"
            />
          </SectionCard>
        </Col>
        {isServicesOfferedCardAllowed && (
          <Col xs={24} xl={12}>
            <div className="services-section">
              <ServicesSettings
                masterServices={services}
                receivedSelectedServices={selectedServices}
                isServicesLoading={isServicesLoading}
                isSelectedServicesLoading={isSelectedServicesLoading}
                addServices={addServices}
                getServices={getServices}
                deleteService={deleteService}
                currentAddedService={addedService}
              />
            </div>
          </Col>
        )}
        {isVolunteerCoordinator && (
          <Col xs={24} xl={12}>
            <AssignedPatientsWrapper workforceIdentifier={identifier} />
          </Col>
        )}
        {isVolunteerCoordinator && (
          <Col xs={24} xl={12}>
            <DocumentsSettings
              userDetails={userDetails}
              workforceIdentifier={identifier}
            />
          </Col>
        )}
      </Row>
      {openModal && (
        <CustomModal
          closable={false}
          isModalOpen={openModal}
          footer={footerButtons}
          closeModal={setOpenModal}
        >
          <div>
            <h5 className="logout-prompt-title">PIN Reset</h5>
            <div>
              The PIN is successfully reset. Please ask{" "}
              <b>
                {getFullName(userDetails?.givenName, userDetails?.familyName)}
              </b>{" "}
              to log in again to proceed.
            </div>
          </div>
        </CustomModal>
      )}
      {openForm && (
        <CustomModal
          closable={false}
          isModalOpen={openForm}
          title="Volunteer Preferences"
          footer={volunteerPreferencesModalFooter}
          closeModal={setOpenForm}
        >
          <VolunteerPrefernceForm
            formId={VOLUNTEER_PREFERENCES_FORM_ID}
            userIdentifier={identifier}
            onLoadingChange={setFormLoading}
            setOpenForm={setOpenForm}
          />
        </CustomModal>
      )}
    </section>
  );
};

export default VolunteerProfilePage;
